<script setup lang="ts">
import {
  computed,
  nextTick,
  onActivated,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { dateFormat } from '@/components/helpers/formats'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import { formatPeriodShortLabel, earliestRecordableDonationIsoDate, isDonationDateChangeAllowed } from '@/helpers/givingRecord'
import { isGiftAidHuntDonation } from '@/helpers/foundForYou'
import { useDetailNavigation } from '@/composables/useDetailNavigation'
import { useRecurringDonations } from '@/composables/useRecurringDonations'
import { isDonationIncludedInClaim } from '@/helpers/claimTaxBack/includedInClaim'
import {
  claimTaxYearConfig,
  useClaimTaxBack,
} from '@/composables/useClaimTaxBack'
import {
  donationSourceDetailBodyKey,
  donationSourceDetailTitleKey,
  isManualDonationSource,
} from '@/helpers/donationSource'
import {
  isDonationGiftAidDeclared,
  isManualGiftAidRecord,
} from '@/helpers/giftAidDeclared'
import { donationIsoToUkDate } from '@/helpers/recurring/dates'
import { recurringDonationsEnabled } from '@/helpers/recurringDonationsEnabled'
import debug from '@/helpers/debug'
import { parseAmountToPence, penceToAmountInput } from '@/helpers/parseAmountToPence'
import {
  DONATION_ANALYTICS_EVENTS,
  trackDonationAnalytics,
} from '@/helpers/donationsAnalytics'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import { useDashboardToast } from '@/composables/useDashboardToast'
import DonationCharityHeader from '@/components/dashboard/ui/DonationCharityHeader.vue'
import ClaimedDonationMark from '@/components/dashboard/ui/ClaimedDonationMark.vue'
import DonationProofAttachments from '@/components/dashboard/donations/DonationProofAttachments.vue'
import DonationGiftAidChoice from '@/components/dashboard/donations/DonationGiftAidChoice.vue'
import DashboardDateInput from '@/components/dashboard/ui/DashboardDateInput.vue'
import DashboardFormField from '@/components/dashboard/ui/DashboardFormField.vue'
import type { DonorDonation } from '@/types/donorFinancialYear'
import type { RecurringFrequency } from '@/types/recurringDonation'

const props = withDefaults(
  defineProps<{
    donation: DonorDonation
    /**
     * Opened from the recurring donation underneath. Back returns there,
     * so this panel does not open that schedule again.
     */
    embedded?: boolean
    /** Keeps ids unique when an earlier detail stays mounted underneath. */
    idPrefix?: string
  }>(),
  { embedded: false, idPrefix: '' }
)

const domId = (id: string) => `${props.idPrefix}${id}`

const emit = defineEmits<{
  deleted: [donationId: string]
  updated: [donation: DonorDonation]
  busy: [busy: boolean]
}>()

const { t } = useI18n()
const { showToast } = useDashboardToast()
const { updateManualDonation, updateManualDonationNotes, deleteManualDonation } =
  useManualGivingDonations()
const { seriesById } = useRecurringDonations()
const recurringEnabled = recurringDonationsEnabled()
const { pushRecurring } = useDetailNavigation()
const { profile } = useClaimTaxBack()

const donation = computed(() => props.donation)
const panelRoot = ref<HTMLElement | null>(null)
const panelScrollTop = ref(0)

const panelScroller = () =>
  panelRoot.value?.querySelector<HTMLElement>('.sa-donation-detail-panel__scroll') ??
  null

// Detaching the view clears its scroll position, so remember it while it moves.
onMounted(() => {
  const scroller = panelScroller()
  scroller?.addEventListener('scroll', () => {
    if (!scroller.isConnected) return
    panelScrollTop.value = scroller.scrollTop
  })
})
onActivated(() => {
  void nextTick(() => {
    const top = panelScrollTop.value
    const scroller = panelScroller()
    if (scroller) scroller.scrollTop = top
  })
})
const isEditing = ref(false)
const isSaving = ref(false)

const notesDraft = ref('')
const isSavingNotes = ref(false)
const charityDraft = ref('')
const dateDraft = ref('')
const amountDraft = ref('')
const giftAidDraft = ref<boolean | null>(null)

const fieldErrors = reactive({
  charity: '',
  date: '',
  amount: '',
  giftAid: '',
})

const isManual = computed(
  () => donation.value && isManualDonationSource(donation.value.source)
)

const isNotesEditable = computed(() => isManual.value && !isEditing.value)

const recurringSeries = computed(() =>
  seriesById(donation.value?.recurringSeriesId)
)

const frequencyLabel = (frequency: RecurringFrequency) => {
  const keys: Record<RecurringFrequency, string> = {
    weekly: 'Dashboard.Giving.AddDonationModal.FrequencyWeekly',
    monthly: 'Dashboard.Giving.AddDonationModal.FrequencyMonthly',
    quarterly: 'Dashboard.Giving.AddDonationModal.FrequencyQuarterly',
    annually: 'Dashboard.Giving.AddDonationModal.FrequencyAnnually',
  }
  return t(keys[frequency]).toLowerCase()
}

const showProofSection = computed(
  () => (donation.value?.receipts?.length ?? 0) > 0
)

const showRetrospectiveNote = computed(
  () =>
    donation.value?.retrospective === true &&
    Boolean(donation.value.issuedStatementPeriod?.trim())
)

const retrospectiveYearLabel = computed(() => {
  const period = donation.value?.issuedStatementPeriod
  return period ? formatPeriodShortLabel(period) : ''
})

const isDeleting = ref(false)
const showDeleteConfirm = ref(false)
const deleteConfirmButtonRef = ref<HTMLButtonElement | null>(null)
const isBusy = computed(
  () => isSavingNotes.value || isDeleting.value || isSaving.value
)

// The parent disables Close from this event. Exposing isBusy on a template ref
// and reading it in the parent's render loops, because the ref is assigned
// during that same render.
watch(isBusy, (busy) => {
  emit('busy', busy)
})

const sourceTitle = computed(() => {
  if (!donation.value) return ''
  if (recurringSeries.value) {
    return t('Dashboard.DonationDetailModal.Source.recurring.Title')
  }
  if (isGiftAidHuntDonation(donation.value)) {
    return t('Dashboard.GiftAidHunt.SourceTitle')
  }
  const titleKey = donationSourceDetailTitleKey(donation.value)
  if (titleKey) {
    return t(titleKey)
  }
  return donation.value.source?.trim() ?? ''
})

const sourceBody = computed(() => {
  if (!donation.value) return ''
  if (recurringSeries.value) {
    return t('Dashboard.DonationDetailModal.Source.recurring.Body', {
      frequency: frequencyLabel(recurringSeries.value.frequency),
      charity: recurringSeries.value.charity,
    })
  }
  if (isGiftAidHuntDonation(donation.value)) {
    return t('Dashboard.DonationDetailModal.Source.giftAidHunt.Body', {
      method: donation.value.matchMethod?.trim() ?? donation.value.source?.trim() ?? '',
    })
  }
  return t(donationSourceDetailBodyKey(donation.value), {
    platform: donation.value.source?.trim() ?? '',
  })
})

const nextRecurringDateLabel = computed(() => {
  const next = recurringSeries.value?.nextDonationDate
  if (!next || recurringSeries.value?.status !== 'active') return ''
  return t('Dashboard.DonationDetailModal.Source.recurring.NextDonation', {
    date: dateFormat(next),
  })
})

const readOnlyNoteText = computed(() => donation.value?.notes?.trim() ?? '')

const showGiftAidRow = computed(() => {
  if (!donation.value) return false
  if (isManualGiftAidRecord(donation.value)) return true
  return donation.value.giftaid > 0
})

const giftAidFactLabel = computed(() => {
  if (!donation.value) return ''
  if (donation.value.giftaid > 0) {
    return `+${formatMoneyFromPence(donation.value.giftaid)} Gift Aid`
  }
  return t('Dashboard.Giving.Table.NoGiftAid')
})

const includedInClaim = computed(() => {
  if (!donation.value) return false
  return isDonationIncludedInClaim(donation.value, profile.value)
})

/** Shown on the fact list: N/A until this gift is in a year marked claimed. */
const claimTaxBackFactLabel = computed(() => {
  if (includedInClaim.value) {
    return t('Dashboard.DonationDetailModal.ClaimTaxBackClaimed', {
      year: claimTaxYearConfig.yearLabel,
    })
  }
  return t('Dashboard.DonationDetailModal.ClaimTaxBackNa')
})

const notesDirty = computed(() => {
  if (!donation.value || !isNotesEditable.value) return false
  const saved = donation.value.notes?.trim() ?? ''
  return notesDraft.value.trim() !== saved
})

const populateEditForm = () => {
  if (!donation.value) return
  charityDraft.value = donation.value.charity
  dateDraft.value = donationIsoToUkDate(donation.value.time)
  amountDraft.value = penceToAmountInput(donation.value.amount)
  giftAidDraft.value = isDonationGiftAidDeclared(donation.value)
  notesDraft.value = donation.value.notes ?? ''
  fieldErrors.charity = ''
  fieldErrors.date = ''
  fieldErrors.amount = ''
  fieldErrors.giftAid = ''
}

watch(dateDraft, () => {
  fieldErrors.date = ''
})

// A refetch replaces the object but not the gift. Reset only when the id changes,
// so an unsaved note survives Manage series and a series save.
watch(
  () => donation.value?.id,
  () => {
    notesDraft.value = donation.value?.notes ?? ''
    showDeleteConfirm.value = false
    isEditing.value = false
  },
  { immediate: true }
)

const startEditing = () => {
  populateEditForm()
  isEditing.value = true
  showDeleteConfirm.value = false
}

const cancelEditing = () => {
  isEditing.value = false
  notesDraft.value = donation.value?.notes ?? ''
}

const requestDeleteConfirm = async () => {
  showDeleteConfirm.value = true
  await nextTick()
  deleteConfirmButtonRef.value?.focus()
}

const cancelDeleteConfirm = () => {
  showDeleteConfirm.value = false
}

const saveNotes = async () => {
  if (!donation.value || !isNotesEditable.value || !notesDirty.value) return

  isSavingNotes.value = true
  try {
    const updated = await updateManualDonationNotes(
      donation.value.id,
      notesDraft.value
    )
    emit('updated', updated)
    notesDraft.value = updated.notes ?? ''
    showToast(t('Dashboard.DonationDetailModal.NotesSaved'))
  } catch (error) {
    console.error('Failed to update donation notes', error)
    showToast(t('Dashboard.DonationDetailModal.NotesSaveFailed'))
  } finally {
    isSavingNotes.value = false
  }
}

const editDateMin = computed(() => {
  if (!donation.value) return undefined
  const original = donationIsoToUkDate(donation.value.time)
  const earliest = earliestRecordableDonationIsoDate()
  // A gift already older than the cutoff stays editable. The picker must not
  // trap that date; a new date is still checked on save.
  if (original < earliest) return undefined
  return earliest
})

const saveEdits = async () => {
  if (!donation.value || !isManual.value) return

  fieldErrors.charity = ''
  fieldErrors.date = ''
  fieldErrors.amount = ''
  fieldErrors.giftAid = ''

  const trimmedCharity = charityDraft.value.trim()
  if (!trimmedCharity) {
    fieldErrors.charity = t('Dashboard.Giving.AddDonationModal.Toast.MissingCharity')
    return
  }
  if (!dateDraft.value) {
    fieldErrors.date = t('Dashboard.Giving.AddDonationModal.Toast.MissingDate')
    return
  }
  // Only a changed date is sent. An older gift can still be corrected
  // without the API treating its existing day as a new date.
  const dateChanged =
    dateDraft.value !== donationIsoToUkDate(donation.value.time)
  if (
    dateChanged &&
    !isDonationDateChangeAllowed(dateDraft.value, donation.value.time)
  ) {
    debug('Rejected an edited donation date before the 6 tax year cutoff', {
      donationId: donation.value.id,
      date: dateDraft.value,
    })
    fieldErrors.date = t('Dashboard.Giving.AddDonationModal.Toast.DateTooOld', {
      date: dateFormat(earliestRecordableDonationIsoDate()) ?? '',
    })
    return
  }
  const amountPence = parseAmountToPence(amountDraft.value)
  if (amountPence === null) {
    fieldErrors.amount = t('Dashboard.Giving.AddDonationModal.Toast.MissingAmount')
    return
  }
  if (giftAidDraft.value === null) {
    fieldErrors.giftAid = t('Dashboard.Giving.AddDonationModal.Toast.MissingGiftAid')
    return
  }

  isSaving.value = true
  try {
    const updated = await updateManualDonation(donation.value.id, {
      charity: trimmedCharity,
      ...(dateChanged ? { date: dateDraft.value } : {}),
      amountPence,
      giftAidDeclared: giftAidDraft.value,
      notes: notesDraft.value,
    })
    emit('updated', updated)
    isEditing.value = false
    trackDonationAnalytics(DONATION_ANALYTICS_EVENTS.DonationEdited)
    showToast(t('Dashboard.DonationDetailModal.Edited'))
  } catch (error) {
    console.error('Failed to update donation', error)
    showToast(t('Dashboard.DonationDetailModal.EditFailed'))
  } finally {
    isSaving.value = false
  }
}

const deleteDonation = async () => {
  if (!donation.value || !isManual.value || !showDeleteConfirm.value) return

  const donationId = donation.value.id
  isDeleting.value = true
  try {
    await deleteManualDonation(donationId)
    showToast(t('Dashboard.DonationDetailModal.Deleted'))
    emit('deleted', donationId)
  } catch (error) {
    console.error('Failed to delete donation', error)
    showToast(t('Dashboard.DonationDetailModal.DeleteFailed'))
  } finally {
    isDeleting.value = false
  }
}

const occurrenceEditNote = computed(() =>
  t('Dashboard.DonationDetailModal.OccurrenceEditNote')
)

const openRecurringSeries = () => {
  const seriesId = donation.value?.recurringSeriesId
  // A delete in flight must not push the schedule on top of the gift being removed.
  if (!seriesId || props.embedded || isBusy.value) return
  debug('Opening recurring donation from an individual donation', {
    donationId: donation.value?.id,
    seriesId,
    fromEdit: isEditing.value,
  })
  // Stay in this modal. Back returns to this gift.
  pushRecurring(seriesId)
}

</script>

<template>
  <div ref="panelRoot" class="sa-donation-detail-panel">
    <div class="sa-donation-detail-panel__scroll">
      <DonationCharityHeader
        :title-id="domId('donation-detail-title')"
        :charity="donation.charity"
        :logo-url="donation.charityLogoUrl"
        :brand-color="donation.charityBrandColor"
        :subtitle="
          recurringSeries ? $t('Dashboard.DonationDetailModal.Source.recurring.Title') : undefined
        "
      />

      <template v-if="isEditing">
        <div
          v-if="recurringSeries"
          class="sa-donation-detail-modal__edit-context"
        >
          <p class="sa-donation-detail-modal__scope-note">
            {{ occurrenceEditNote }}
          </p>
            <button
              v-if="recurringEnabled && !embedded"
              type="button"
              class="sa-quiet-link sa-donation-detail-modal__manage-link"
              :disabled="isBusy"
              @click="openRecurringSeries"
            >
            {{ $t('Dashboard.DonationDetailModal.Source.recurring.ManageSeries') }}
          </button>
        </div>
        <!-- Section: what was given. -->
        <div class="sa-form-section">
        <DashboardFormField
          v-if="!recurringSeries"
          :label="$t('Dashboard.Giving.AddDonationModal.CharityLabel')"
          :label-for="domId('donation-edit-charity')"
          :error="fieldErrors.charity"
        >
          <template #default="{ errorId, hasError }">
            <input
              :id="domId('donation-edit-charity')"
              v-model="charityDraft"
              class="sa-input"
              type="text"
              autocomplete="organization"
              :aria-invalid="hasError || undefined"
              :aria-describedby="hasError ? errorId : undefined"
            />
          </template>
        </DashboardFormField>
        <div class="sa-form-row">
          <DashboardFormField
            :label="$t('Dashboard.Giving.AddDonationModal.DateLabel')"
            :label-for="domId('donation-edit-date')"
            :error="fieldErrors.date"
          >
            <template #default="{ errorId, hasError }">
              <DashboardDateInput
                :id="domId('donation-edit-date')"
                v-model="dateDraft"
                :min="editDateMin"
                :aria-invalid="hasError || undefined"
                :aria-describedby="hasError ? errorId : undefined"
              />
            </template>
          </DashboardFormField>
          <DashboardFormField
            :label="$t('Dashboard.Giving.AddDonationModal.AmountLabel')"
            :label-for="domId('donation-edit-amount')"
            :error="fieldErrors.amount"
          >
            <template #default="{ errorId, hasError }">
              <input
                :id="domId('donation-edit-amount')"
                v-model="amountDraft"
                class="sa-input"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                :aria-invalid="hasError || undefined"
                :aria-describedby="hasError ? errorId : undefined"
              />
            </template>
          </DashboardFormField>
        </div>
        </div>

        <!-- Section: Gift Aid. -->
        <div class="sa-form-section">
          <DonationGiftAidChoice
            v-model="giftAidDraft"
            :input-name="domId('donation-edit-gift-aid')"
            :yes-input-id="domId('donation-edit-gift-aid-yes')"
            :no-input-id="domId('donation-edit-gift-aid-no')"
            :error="fieldErrors.giftAid"
          />
        </div>

        <!-- Section: notes. -->
        <div class="sa-form-section">
          <DashboardFormField
            :label="$t('Dashboard.Giving.AddDonationModal.NotesLabel')"
            :label-for="domId('donation-edit-notes')"
          >
            <template #default>
              <textarea
                :id="domId('donation-edit-notes')"
                v-model="notesDraft"
                class="sa-input sa-donation-detail-modal__notes-input"
                rows="3"
              />
            </template>
          </DashboardFormField>
        </div>
      </template>

      <template v-else>
        <dl class="sa-donation-detail-modal__facts">
          <div class="sa-detail-row">
            <dt class="sa-detail-label">{{ $t('Dashboard.DonationDetailModal.Date') }}</dt>
            <dd class="sa-detail-value">{{ dateFormat(donation.time) }}</dd>
          </div>
          <div class="sa-detail-row">
            <dt class="sa-detail-label">{{ $t('Dashboard.DonationDetailModal.Amount') }}</dt>
            <dd class="sa-detail-value">{{ formatMoneyFromPence(donation.amount) }}</dd>
          </div>
          <div v-if="showGiftAidRow" class="sa-detail-row">
            <dt class="sa-detail-label">{{ $t('Dashboard.DonationDetailModal.GiftAid') }}</dt>
            <dd class="sa-detail-value">{{ giftAidFactLabel }}</dd>
          </div>
          <div class="sa-detail-row">
            <dt class="sa-detail-label">
              {{ $t('Dashboard.DonationDetailModal.IncludedInClaimHeading') }}
            </dt>
            <dd
              class="sa-detail-value sa-donation-detail-modal__claim-value"
              :class="{
                'sa-donation-detail-modal__claim-tax-back': includedInClaim,
              }"
            >
              <ClaimedDonationMark v-if="includedInClaim" icon-only />
              <span>{{ claimTaxBackFactLabel }}</span>
            </dd>
          </div>
        </dl>

        <section class="sa-section sa-donation-detail-modal__source" :aria-labelledby="domId('donation-source-heading')">
          <h3 :id="domId('donation-source-heading')" class="sa-modal-section-heading">
            {{ $t('Dashboard.DonationDetailModal.SourceHeading') }}
          </h3>
          <div class="sa-donation-detail-modal__source-card">
            <strong class="sa-donation-detail-modal__source-title">{{ sourceTitle }}</strong>
            <div class="sa-donation-detail-modal__source-body">{{ sourceBody }}</div>
            <p
              v-if="nextRecurringDateLabel"
              class="sa-donation-detail-modal__source-next"
            >
              {{ nextRecurringDateLabel }}
            </p>
            <button
              v-if="recurringEnabled && recurringSeries && !embedded"
              type="button"
              class="sa-quiet-link sa-donation-detail-modal__manage-link"
              :disabled="isBusy"
              @click="openRecurringSeries"
            >
              {{ $t('Dashboard.DonationDetailModal.Source.recurring.ManageSeries') }}
            </button>
          </div>
        </section>

        <p
          v-if="showRetrospectiveNote"
          class="sa-section sa-donation-detail-modal__retrospective-note"
          role="note"
        >
          {{
            $t('Dashboard.DonationDetailModal.RetrospectiveStatementNote', {
              year: retrospectiveYearLabel,
            })
          }}
        </p>

        <section
          v-if="isNotesEditable"
          class="sa-section sa-donation-detail-modal__notes-block"
          :aria-labelledby="domId('donation-notes-heading')"
        >
          <h3 :id="domId('donation-notes-heading')" class="sa-modal-section-heading">
            {{ $t('Dashboard.DonationDetailModal.Notes') }}
          </h3>
          <textarea
            :id="domId('donation-detail-notes')"
            v-model="notesDraft"
            class="sa-input sa-donation-detail-modal__notes-input"
            rows="3"
            :placeholder="$t('Dashboard.DonationDetailModal.NotesPlaceholder')"
            :disabled="isBusy"
            :aria-labelledby="domId('donation-notes-heading')"
          />
          <div class="sa-donation-detail-modal__notes-actions">
            <button
              type="button"
              class="sa-btn-secondary sa-btn-secondary--compact"
              :disabled="!notesDirty || isBusy"
              @click="saveNotes"
            >
              {{ $t('Dashboard.DonationDetailModal.SaveNotes') }}
            </button>
          </div>
        </section>

        <section
          v-else-if="readOnlyNoteText"
          class="sa-section sa-donation-detail-modal__notes-readonly"
          :aria-labelledby="domId('donation-notes-readonly-heading')"
        >
          <h3 :id="domId('donation-notes-readonly-heading')" class="sa-modal-section-heading">
            {{ $t('Dashboard.DonationDetailModal.Notes') }}
          </h3>
          <p class="sa-donation-detail-modal__notes">{{ readOnlyNoteText }}</p>
        </section>

        <DonationProofAttachments
          v-if="showProofSection"
          class="sa-section"
          :receipts="donation.receipts ?? []"
        />
      </template>
    </div>

    <div v-if="isManual" class="sa-modal__footer">
      <div
        v-if="isEditing"
        class="sa-donation-detail-modal__footer-actions"
      >
        <button
          type="button"
          class="sa-btn-secondary"
          :disabled="isBusy"
          @click="cancelEditing"
        >
          {{ $t('Buttons.Cancel') }}
        </button>
        <button
          type="button"
          class="sa-btn-primary"
          :disabled="isBusy"
          :aria-busy="isSaving"
          @click="saveEdits"
        >
          {{ $t('Dashboard.DonationDetailModal.SaveEdits') }}
        </button>
      </div>
      <div
        v-else-if="!showDeleteConfirm"
        class="sa-donation-detail-modal__footer-actions"
      >
        <button
          type="button"
          class="sa-btn-secondary"
          :disabled="isBusy"
          @click="startEditing"
        >
          {{ $t('Dashboard.DonationDetailModal.EditDonation') }}
        </button>
        <button
          type="button"
          class="sa-btn-secondary sa-btn-secondary--danger"
          :disabled="isBusy"
          @click="requestDeleteConfirm"
        >
          {{ $t('Dashboard.DonationDetailModal.DeleteDonation') }}
        </button>
      </div>
      <div
        v-else
        class="sa-donation-detail-modal__delete-confirm"
        role="group"
        :aria-label="$t('Dashboard.DonationDetailModal.DeleteDonation')"
        :aria-describedby="domId('donation-delete-confirm-intro')"
      >
        <p :id="domId('donation-delete-confirm-intro')" class="sa-donation-detail-modal__delete-confirm-intro">
          {{ $t('Dashboard.DonationDetailModal.DeleteConfirmIntro') }}
        </p>
        <div class="sa-donation-detail-modal__footer-actions">
          <button
            type="button"
            class="sa-btn-secondary"
            :disabled="isDeleting"
            @click="cancelDeleteConfirm"
          >
            {{ $t('Dashboard.DonationDetailModal.KeepDonation') }}
          </button>
          <button
            ref="deleteConfirmButtonRef"
            type="button"
            class="sa-btn-secondary sa-btn-secondary--danger"
            :disabled="isDeleting"
            @click="deleteDonation"
          >
            {{
              isDeleting
                ? $t('Dashboard.DonationDetailModal.Deleting')
                : $t('Dashboard.DonationDetailModal.ConfirmDelete')
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
